import { useState } from 'react';
import { motion } from 'motion/react';
import { Download, Lock, Unlock, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { useBulkRestore, useBulkSuspend } from '../hooks';
import { useShallow } from 'zustand/react/shallow';
import { selectUserQuery, useUserFilters } from '../store/filters.store';
import { bulkSuspendSchema, type BulkSuspendInput } from '../schemas';
import { exportUsersCsvUrl } from '../services/user.service';

export function BulkActionsBar() {
  const selectedIds = useUserFilters((s) => s.selectedIds);
  const clearSelected = useUserFilters((s) => s.clearSelected);
  const query = useUserFilters(useShallow(selectUserQuery));
  const [showSuspend, setShowSuspend] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const bulkSuspend = useBulkSuspend();
  const bulkRestore = useBulkRestore();
  const form = useForm<BulkSuspendInput>({
    resolver: zodResolver(bulkSuspendSchema),
    defaultValues: { reason: '' },
  });

  if (selectedIds.length === 0) return null;

  const submitSuspend = form.handleSubmit(async (values) => {
    await bulkSuspend.mutateAsync({ ids: selectedIds, reason: values.reason });
    clearSelected();
    setShowSuspend(false);
    form.reset();
  });

  return (
    <>
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 glass-panel rounded-full px-4 py-2 flex items-center gap-3"
      >
        <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">
          {selectedIds.length} selected
        </span>
        <button
          onClick={() => setShowSuspend(true)}
          className="px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-amber-500/30"
        >
          <Lock className="w-3 h-3" /> Suspend
        </button>
        <button
          onClick={() => setShowRestore(true)}
          className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-emerald-500/30"
        >
          <Unlock className="w-3 h-3" /> Restore
        </button>
        <a
          href={exportUsersCsvUrl(query)}
          className="px-3 py-1.5 rounded-full bg-electric-violet/20 text-primary text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-electric-violet/30"
        >
          <Download className="w-3 h-3" /> Export
        </a>
        <button
          onClick={() => clearSelected()}
          className="text-on-surface-variant hover:text-on-surface ml-1"
          aria-label="Clear selection"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>

      <ConfirmModal
        open={showSuspend}
        title={`Suspend ${selectedIds.length} users`}
        confirmLabel="Suspend all"
        destructive
        loading={bulkSuspend.isPending}
        onClose={() => setShowSuspend(false)}
        onConfirm={() => void submitSuspend()}
      >
        <form onSubmit={submitSuspend} className="space-y-2">
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
            Reason
          </label>
          <textarea
            {...form.register('reason')}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
            placeholder="Reason applied to every suspension audit log (min 10 chars)"
          />
          {form.formState.errors.reason && (
            <p className="text-xs text-red-300">{form.formState.errors.reason.message}</p>
          )}
        </form>
      </ConfirmModal>

      <ConfirmModal
        open={showRestore}
        title={`Restore ${selectedIds.length} users`}
        description="Each selected user will regain access immediately."
        confirmLabel="Restore all"
        loading={bulkRestore.isPending}
        onClose={() => setShowRestore(false)}
        onConfirm={async () => {
          await bulkRestore.mutateAsync(selectedIds);
          clearSelected();
          setShowRestore(false);
        }}
      />
    </>
  );
}
